<template>
  <svg
    alt=""
    aria-hidden="true"
    class="w-full"
    height="170"
    :viewBox="`0 0 420 170`"
    :width="420"
    xmlns="http://www.w3.org/2000/svg"
  >
    <!-- Card -->
    <rect
      fill="#ede9f8"
      height="68"
      rx="16"
      stroke="#d0c4f0"
      stroke-width="1.5"
      :width="420"
      x="0"
      y="50"
    />

    <!-- Bullet -->
    <circle cx="24" cy="84" fill="#333" r="4" />

    <!-- Text segments -->
    <text
      class="text-action-default-hi body-1-bold"
      fill="currentColor"
      lengthAdjust="spacingAndGlyphs"
      textLength="50"
      x="40"
      y="89"
    >
      Core i5
    </text>
    <text
      class="text-action-default-hi body-1-bold"
      fill="currentColor"
      lengthAdjust="spacingAndGlyphs"
      textLength="14"
      x="90"
      y="89"
    >
      -
    </text>
    <text
      class="text-action-default-hi body-1-bold"
      fill="currentColor"
      lengthAdjust="spacingAndGlyphs"
      textLength="42"
      x="104"
      y="89"
    >
      Gen 8
    </text>
    <text
      class="text-action-default-hi body-1-bold"
      fill="currentColor"
      lengthAdjust="spacingAndGlyphs"
      textLength="5"
      x="146"
      y="89"
    ></text>
    <text
      class="text-action-default-hi body-1-bold"
      fill="currentColor"
      lengthAdjust="spacingAndGlyphs"
      textLength="22"
      x="151"
      y="89"
    >
      (U)
    </text>

    <!-- Blurred placeholder -->
    <rect
      fill="#c5bde0"
      height="18"
      opacity="0.5"
      rx="4"
      width="70"
      x="330"
      y="75"
    />

    <!-- ── Series overbrace ── -->
    <line stroke="#7c3aed" stroke-width="1.5" x1="40" x2="90" y1="62" y2="62" />
    <line stroke="#7c3aed" stroke-width="1.5" x1="40" x2="40" y1="62" y2="69" />
    <line stroke="#7c3aed" stroke-width="1.5" x1="90" x2="90" y1="62" y2="69" />
    <line stroke="#7c3aed" stroke-width="1.5" x1="65" x2="65" y1="30" y2="62" />

    <!-- ── Power class overbrace ── -->
    <line
      stroke="#7c3aed"
      stroke-width="1.5"
      x1="151"
      x2="173"
      y1="62"
      y2="62"
    />
    <line
      stroke="#7c3aed"
      stroke-width="1.5"
      x1="151"
      x2="151"
      y1="62"
      y2="69"
    />
    <line
      stroke="#7c3aed"
      stroke-width="1.5"
      x1="173"
      x2="173"
      y1="62"
      y2="69"
    />
    <line
      stroke="#7c3aed"
      stroke-width="1.5"
      x1="162"
      x2="162"
      y1="30"
      y2="62"
    />

    <!-- ── Generation underbrace ── -->
    <line
      stroke="#7c3aed"
      stroke-width="1.5"
      x1="104"
      x2="146"
      y1="106"
      y2="106"
    />
    <line
      stroke="#7c3aed"
      stroke-width="1.5"
      x1="104"
      x2="104"
      y1="99"
      y2="106"
    />
    <line
      stroke="#7c3aed"
      stroke-width="1.5"
      x1="146"
      x2="146"
      y1="99"
      y2="106"
    />
    <line
      stroke="#7c3aed"
      stroke-width="1.5"
      x1="125"
      x2="125"
      y1="106"
      y2="134"
    />

    <!-- Series pill -->
    <rect
      fill="#7c3aed"
      height="30"
      rx="15"
      :width="pills.series.width"
      :x="pills.series.x"
      y="0"
    />
    <text
      ref="seriesPillRef"
      class="body-1-bold text-static-light-hi"
      fill="currentColor"
      text-anchor="middle"
      :x="pills.series.x + pills.series.width / 2"
      y="20"
    >
      {{ i18n(translations.section2Subtitle1) }}
    </text>

    <!-- Power class pill (left edge at x=162) -->
    <rect
      fill="#7c3aed"
      height="30"
      rx="15"
      :width="pills.power.width"
      x="150"
      y="0"
    />
    <text
      ref="powerPillRef"
      class="body-1-bold text-static-light-hi"
      fill="currentColor"
      text-anchor="middle"
      :x="150 + pills.power.width / 2"
      y="20"
    >
      {{ i18n(translations.section2Subtitle3) }}
    </text>

    <!-- Generation pill -->
    <rect
      fill="#7c3aed"
      height="30"
      rx="15"
      :width="pills.gen.width"
      :x="pills.gen.x"
      y="134"
    />
    <text
      ref="genPillRef"
      class="body-1-bold text-static-light-hi"
      fill="currentColor"
      text-anchor="middle"
      :x="pills.gen.x + pills.gen.width / 2"
      y="154"
    >
      {{ i18n(translations.section2Subtitle2) }}
    </text>
  </svg>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import translations from '../LaptopProcessorModalBody/LaptopProcessorModalBody.translations'

const PADDING = 12

const seriesPillRef = ref(null)
const genPillRef = ref(null)
const powerPillRef = ref(null)

const i18n = useI18n()

const pills = reactive({
  series: { x: 15, width: 100 },
  gen: { x: 57, width: 135 },
  power: { width: 145 },
})

onMounted(() => {
  const measure = (el, centerX) => {
    if (!el) return null
    const width = el.getBBox().width + PADDING * 2

    return { width, x: centerX - width / 2 }
  }

  const series = measure(seriesPillRef.value, 65)
  const gen = measure(genPillRef.value, 125)
  const power = measure(powerPillRef.value, 162)

  if (series) {
    pills.series.width = series.width
    pills.series.x = series.x
  }
  if (gen) {
    pills.gen.width = gen.width
    pills.gen.x = gen.x
  }
  if (power) {
    pills.power.width = power.width
  }
})
</script>
